import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { CartModifierGroup } from '../utils/cart';
import { TopLevelMenuItem } from '../utils/menu';
import { GenericMap } from '../utils/types';
import { getModSymbolFromInput } from '../utils/autocomplete';
import { convertToMap, convertToMapUsingName } from '../utils';
import { ParsedMenuItem, ParsedModifierGroup } from '../types/menu';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { canShowBMItem } from '../components/ItemBuilder/menu/modGroup.utils';
import {
  hasExpressModePeriods,
  isExpressModeActive,
} from '../utils/expressMode';
import {
  checkConstraintAvailabilityAndActive,
  getConstraintApplicableGroups,
  getRestaurantGroups,
} from '../utils/menuUnification.utils';
import {
  selectActiveRestaurantCode,
  selectActiveRestaurantTimezone,
  selectPrimaryRestaurantCode,
} from './restaurant';
import { TOTAL_READBACK_CONSTRAINT_PREFIX } from '../constants';
import {
  selectFeatureFlagDoNotUpsellInExpressMode,
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
} from '../redux/features/config/config.selector';
import { selectIsExpressTimerElapsed } from '../redux/features/taskRouter/taskRouter.selector';

const selectMenu = (state: RootState) => state.menu;

export function getModSymbolMapping(state: RootState) {
  return state.menu.modSymbolMapping;
}

export function getCodeNameMapping(state: RootState) {
  return state.menu.codeNameMapping;
}

export function generateMenuItemsSelector(currentMessage: string) {
  return (state: RootState): (ParsedMenuItem | TopLevelMenuItem)[] => {
    const {
      cart: { cartItems },
      dialog: {
        selectedItem: dialogSelectedItem,
        selectedItemCartId,
        selectedModGroup: dialogSelectedModGroup,
      },
      menu: { modSymbolMapping, topLevelMenuItems, unavailableItemsByName },
    } = state;
    const cartItemsValues = Object.values(cartItems);
    const selectedItem =
      dialogSelectedItem || cartItemsValues[cartItemsValues.length - 1];
    let currentItems: (ParsedMenuItem | TopLevelMenuItem)[] =
      Object.values(topLevelMenuItems);

    let currentMods: (ParsedMenuItem | TopLevelMenuItem)[] = [];
    const isUnifiedMenu = getMenuUnificationFeatureFlag();

    if (selectedItem) {
      let modGroups: ParsedModifierGroup[] = [];
      let childrenMods: GenericMap<CartModifierGroup>;
      if (dialogSelectedModGroup) {
        modGroups = [dialogSelectedModGroup];
        const inChildModGroups =
          cartItems[selectedItemCartId]?.childModifierGroups[
            dialogSelectedModGroup?.id || ''
          ];
        childrenMods = inChildModGroups
          ? convertToMap([Object.assign(inChildModGroups)])
          : {};
      } else {
        modGroups = Object.values(selectedItem.modifierGroups);
        childrenMods = Object.assign(
          cartItems[selectedItemCartId]?.childModifierGroups || {}
        );
      }
      recurviselyGetMods(childrenMods, modGroups);

      const updatedCartItem = {
        ...selectedItem,
        childModifierGroups: childrenMods,
      };
      modGroups.forEach((modGroup) => {
        const modGroupMenuItems = Object.values(modGroup.menuItems).filter(
          (menuItem) => {
            if (isUnifiedMenu) {
              return canShowBMItem({
                unavailableItemsByName,
                itemName: menuItem.name,
                modGroupName: modGroup.name,
                cartItem: updatedCartItem,
              });
            } else {
              return true;
            }
          }
        );
        currentMods = [...currentMods, ...modGroupMenuItems];
        currentItems = [...currentItems, ...modGroupMenuItems];
      });
    }

    const modSymbol = getModSymbolFromInput(currentMessage, modSymbolMapping);
    let itemsList = modSymbol ? currentMods : currentItems;

    return itemsList;
  };
}
function recurviselyGetMods(
  childrenMods: GenericMap<CartModifierGroup>,
  modGroups: ParsedModifierGroup[]
) {
  const children = Object.values(childrenMods || {})
    .flatMap((child) =>
      Object.values(child.selectedItems).sort(
        (itemA, itemB) =>
          (itemA.parentModifierGroupIndex || 0) -
          (itemB.parentModifierGroupIndex || 0)
      )
    )
    .flatMap(({ modifierGroups, childModifierGroups }) => {
      recurviselyGetMods(childModifierGroups, modGroups);
      return Object.values(modifierGroups);
    });
  modGroups.push(...children);
}

export function selectPersistentVoiceProps(state: RootState) {
  return state.menu.persistentVoiceProps;
}

export const selectMenuRes = createSelector(selectMenu, (menu) => menu.menuRes);

export const selectAvailableCategoryWithTimePeriod = createSelector(
  selectMenu,
  (menu) => menu.availableCategoryWithTimePeriod
);

export const selectTopLevelMenuItems = createSelector(
  selectMenu,
  (menu) => menu.topLevelMenuItems
);

export const promptsSelector = createSelector(
  selectTopLevelMenuItems,
  (items) => {
    const menuPrompts = Object.values(items).reduce((acc, item) => {
      if (item.name && item.name.startsWith('/')) {
        const [intent, ...phraseParts] = item.name.split(':');
        const phrase = phraseParts.join(':').trim();
        if (phrase) {
          acc.push({
            intent: intent.slice(1),
            phrase: phrase,
          });
        }
      }
      return acc;
    }, [] as Array<{ intent: string; phrase: string }>);

    return menuPrompts.reduce((acc, item) => {
      acc[item.intent] = item;
      return acc;
    }, {} as Record<string, { intent: string; phrase: string }>);
  }
);

export const selectedMenuVersionSelector = createSelector(
  selectMenu,
  (menu) => menu.selectedMenuVersion
);
export const menuVersionsSelector = createSelector(
  selectMenu,
  (menu) => menu.menuVersions
);
export const prodLiveVersionSelector = createSelector(
  selectMenu,
  (menu) => menu.prodLiveVersion
);

export const posSettingsSelector = createSelector(
  selectMenu,
  (menu) => menu.menuRes?.posSettings || {}
);

export const modSymbolMappingSelector = createSelector(
  selectMenu,
  (menu) => menu.modSymbolMapping
);

export const selectIsBrandMenu = createSelector(
  selectMenu,
  (menu) => !!menu.brandMenu
);

export const selectUnavailableItemsByName = createSelector(
  selectMenu,
  (menu) => menu.unavailableItemsByName
);

export const selectBMItemsSynonyms = createSelector(
  selectMenu,
  (menu) => menu.bmItemsSynonyms
);

export const selectLoadedVersionDetails = createSelector(
  selectMenu,
  (menu) => menu.loadedVersionDetails
);

export const selectBrandMenuVersion = createSelector(
  selectLoadedVersionDetails,
  (loadedVersionDetails) => loadedVersionDetails?.brandMenuVersion
);

export const selectBrandMenu = createSelector(
  selectMenu,
  (menu) => menu.brandMenu
);

export const selectBrandMenuItemsByName = createSelector(
  selectMenu,
  (menu) => menu.brandMenuItemsByName
);

export const selectItemConversionBusinessRules = createSelector(
  selectMenu,
  (menu) => menu.itemConversionBusinessRules
);

export const selectBrandMenuConstraints = createSelector(
  selectBrandMenu,
  (brandMenu) => convertToMapUsingName(brandMenu?.constraints || [])
);

export const selectRestaurantMealPeriodsByName = createSelector(
  selectMenu,
  (menu) => menu.restaurantMealPeriodsByName
);

export const selectIsExpressModeActive = createSelector(
  selectRestaurantMealPeriodsByName,
  selectActiveRestaurantTimezone,
  (restaurantMealPeriodsByName, timezone) =>
    isExpressModeActive(restaurantMealPeriodsByName, timezone)
);

export const selectHasExpressModeMealPeriods = createSelector(
  selectRestaurantMealPeriodsByName,
  hasExpressModePeriods
);

export const selectIsExpressUpsellMode = createSelector(
  selectIsExpressModeActive,
  selectFeatureFlagDoNotUpsellInExpressMode,
  (isActive, ffEnabled) => isActive && ffEnabled
);

// Time-based express mode: hitl_3_0 + hitl_express_timer on AND the order has
// elapsed past max_seconds (latched in taskRouter). Drives "stop all upsells".
export const selectIsExpressTimerActive = createSelector(
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectIsExpressTimerElapsed,
  (hitl3, expressTimerEnabled, elapsed) =>
    hitl3 && expressTimerEnabled && elapsed
);

export const selectTotalConstraint = createSelector(
  selectBrandMenuConstraints,
  selectActiveRestaurantTimezone,
  selectRestaurantMealPeriodsByName,
  selectBrandMenu,
  selectActiveRestaurantCode,
  selectPrimaryRestaurantCode,
  (
    constraints,
    timezone,
    restaurantMealPeriodsByName,
    brandMenu,
    restaurantCode,
    primaryRestaurantCode
  ) => {
    // Find all constraints that start with 'total_readback' prefix
    const totalReadbackConstraints = Object.keys(constraints || {}).filter(
      (key) => key.startsWith(TOTAL_READBACK_CONSTRAINT_PREFIX)
    );

    // Use primaryRestaurantCode for brand menu group lookups — in mirroring
    // scenarios the selected code differs from the code in restaurant_groups.
    const currentRestaurantGroups = getRestaurantGroups(
      primaryRestaurantCode || restaurantCode || '',
      brandMenu?.restaurant_groups || []
    );

    // Check each constraint for availability and active status
    for (const constraintName of totalReadbackConstraints) {
      const constraint = constraints[constraintName];
      if (constraint?.prompt) {
        const isAvailableAndActive = checkConstraintAvailabilityAndActive({
          mealPeriod: constraint.meal_period || null,
          timezone,
          restaurantMealPeriodsByName,
          constraintName,
        });

        if (!isAvailableAndActive) {
          continue;
        }

        const applicableGroups = getConstraintApplicableGroups(
          constraint.restaurant_groups,
          constraint.meal_period || null,
          restaurantMealPeriodsByName || {},
          currentRestaurantGroups
        );

        if (applicableGroups !== null) {
          continue;
        }

        return {
          'total-template': {
            intent: 'total-template',
            phrase: constraint.prompt,
          },
        };
      }
    }

    return null;
  }
);

export const selectGreetingDetails = createSelector(
  selectMenu,
  (menu) => menu.greetingDetails
);

export const selectIntentCards = createSelector(
  selectMenu,
  (menu) => menu.intentCards
);

export const selectDismissReadbackPhrases = createSelector(
  selectMenu,
  (menu) => menu.dismissReadbackPhrases
);
